import React, { useEffect, useState } from "react";
import { apiClient } from "../../../services/apiService";
import { colors } from "../../../colors";
import { isValidApiCollection } from "./validateApiCollection";
import { useRepoContext } from "./ContractContext";
import { useUser } from "../../../services/useUser";
import { getPermissionClient } from "@merncloud/permission-resolver/frontend";

interface RepoOption {
  label: string;
  value: string;
}

interface VersionMetadata {
  versionId: string;
  fileName: string;
  uploadedAt: string;
  uploadedBy?: string;
}
interface Props {
  goToTab: (tab: string) => void;
  repo?: string;
  activeTab?: string;
}

const resource = {
  id: "1",
  type: "contract",
  attributes: {
    department: "engineering",
  },
};

export default function ApiCollectionManager({
  goToTab,
  repo,
  activeTab,
}: Props) {
  const subject = useUser();
  const canRead = getPermissionClient().can(subject, resource, "read");
  const canUpload = getPermissionClient().can(subject, resource, "upload");
  const canDownload = getPermissionClient().can(subject, resource, "download");
  const [repos, setRepos] = useState<RepoOption[]>([]);
  const [selectedRepo, setSelectedRepo] = useState<string>(repo || "");
  const [versions, setVersions] = useState<VersionMetadata[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const { setSelectedRepo: setRepo, setSelectedVersion: setVersion } =
    useRepoContext();

  const fetchRepos = async () => {
    const response = await apiClient.get("/api-contract/api-list");
    setRepos(response.data.map((r: string) => ({ label: r, value: r })));
  };

  const fetchVersions = async (repoId: string) => {
    const response = await apiClient.get(
      `/api-contract/file/${repoId}/versions`
    );
    const sortedVersions = response.data.versions.sort(
      (a: VersionMetadata, b: VersionMetadata) =>
        new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime()
    );
    setVersions(sortedVersions);
  };

  const handleUpload = async () => {
    // if (!canUpload) {
    //   alert("You do not have permission to upload API collections.");
    //   return;
    // }
    if (!file || !selectedRepo) return;
    const isValid = await isValidApiCollection(file);
    if (!isValid) {
      alert("Only valid Postman API collection files are allowed.");
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await apiClient.post(
        `/api-contract/file/${selectedRepo}/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const versionId = response.data.versionMeta?.versionId;
      alert(`Upload successful.\nVersion ID: ${versionId || "Unknown"}`);

      setFile(null);
      await fetchVersions(selectedRepo);
    } catch (error) {
      alert("Upload failed");
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    fetchRepos();
    if (repo) {
      setSelectedRepo(repo);
      fetchVersions(repo);
    }
  }, [activeTab]);

  useEffect(() => {
    if (selectedRepo) {
      fetchVersions(selectedRepo);
    } else {
      setVersions([]);
    }
  }, [selectedRepo]);

  const filteredVersions = versions.filter((v) =>
    [v.versionId, v.uploadedBy]
      .join(" ")
      .toLowerCase()
      .includes(searchTerm.trim().toLowerCase())
  );

  const handleViewDoc = (r: string, v: string) => {
    setRepo(r);
    setVersion(v);
    goToTab("tab2");
  };

  return (
    <div className="">
      <select
        className="border rounded px-4 py-2 mb-4 w-full"
        value={selectedRepo}
        onChange={(e) => {
          setSelectedRepo(e.target.value);
          setSearchTerm(""); // Clear search when switching repo
        }}
      >
        <option value="">Select an API</option>
        {repos.map((repo) => (
          <option key={repo.value} value={repo.value}>
            {repo.label}
          </option>
        ))}
      </select>

      {selectedRepo && (
        <>
          <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-4">
            <input
              type="file"
              accept=".json"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="sm:w-auto"
            />
            <button
              onClick={handleUpload}
              className="px-4 py-1 rounded disabled:opacity-50"
              style={{
                backgroundColor: colors.filterButtonsActive,
                color: colors.filterButtonsText,
              }}
              disabled={uploading || !file}
            >
              {uploading ? "Uploading..." : "Upload Collection"}
            </button>
          </div>

          <input
            type="text"
            placeholder="Search by Version ID or Uploaded By"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="border px-4 py-2 rounded w-full mb-4"
          />

          <div>
            <h2 className="text-xl font-semibold mb-2">Uploaded Versions</h2>
            <table className="min-w-full border text-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border px-4 py-2 text-left">Version ID</th>
                  <th className="border px-4 py-2 text-left">Uploaded At</th>
                  <th className="border px-4 py-2 text-left">Uploaded By</th>
                  <th className="border px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredVersions.length > 0 ? (
                  filteredVersions.map((v) => (
                    <tr key={v.versionId}>
                      <td className="border px-4 py-2">{v.versionId}</td>
                      <td className="border px-4 py-2">
                        {new Date(v.uploadedAt).toLocaleString()}
                      </td>
                      <td className="border px-4 py-2">
                        {v.uploadedBy || "Unknown"}
                      </td>
                      <td className="border px-4 py-2 text-center">
                        <button
                          onClick={() => {
                            handleViewDoc(selectedRepo, v.versionId);
                          }}
                          className="hover:underline"
                        >
                          View Doc
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={4}
                      className="border px-4 py-4 text-center text-gray-500"
                    >
                      No versions found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
